import apiClient from "@lib/axios";

import type {
  CreateRequirementBody,
  PatchRequirementBody,
  Requirement,
  RequirementsSpec,
  TierUpgradeReport,
  UpdateSpecBody,
} from "../types";

/**
 * REST surface for the Requirements module — backed by
 * `backend/requirements/router.py`. All routes are project-scoped at
 * `/projects/{project_id}/requirements` and require an authenticated
 * session cookie (`apiClient` carries it).
 *
 * 409 on `updateSpec` is special: the server returns the gap report
 * in `error.response.data.detail` so the client can render it without
 * a follow-up GET. We surface a typed error so the hook layer can
 * branch cleanly.
 */

export class TierUpgradeBlockedError extends Error {
  readonly report: TierUpgradeReport;
  constructor(report: TierUpgradeReport) {
    super(
      `Tier upgrade blocked: ${report.gaps.filter((g) => g.blocking).length} ` +
        "blocking gaps must be cleared first.",
    );
    this.name = "TierUpgradeBlockedError";
    this.report = report;
  }
}

const base = (projectId: string) =>
  `/projects/${encodeURIComponent(projectId)}/requirements`;

export const requirementsApi = {
  /** GET — full spec for a project. Lazy-creates the spec row server-
   *  side, so calling this is the canonical "open the surface" gesture. */
  getSpec: async (projectId: string): Promise<RequirementsSpec> => {
    const { data } = await apiClient.get<RequirementsSpec>(base(projectId));
    return data;
  },

  /** POST — server mints both UUID and `REQ-XXX-NNN` public id. */
  create: async (
    projectId: string,
    body: CreateRequirementBody,
  ): Promise<Requirement> => {
    const { data } = await apiClient.post<Requirement>(base(projectId), body);
    return data;
  },

  /** PATCH — sparse update; only the fields the caller sets land on the
   *  audit log delta. */
  patch: async (
    projectId: string,
    publicId: string,
    body: PatchRequirementBody,
  ): Promise<Requirement> => {
    const { data } = await apiClient.patch<Requirement>(
      `${base(projectId)}/${encodeURIComponent(publicId)}`,
      body,
    );
    return data;
  },

  /** DELETE — soft delete; the row stays for the audit trail. The
   *  public_id is never reused (server-side index-enforced). */
  remove: async (projectId: string, publicId: string): Promise<void> => {
    await apiClient.delete(
      `${base(projectId)}/${encodeURIComponent(publicId)}`,
    );
  },

  /** POST — promote a suggested requirement to 'draft'. `sessionId`
   *  (when supplied) carries through so the agent's next turn sees the
   *  acceptance via its `<system-reminder>` since-last-turn block,
   *  replacing the old synthetic '(continue — accepted h1)' message. */
  acceptSuggestion: async (
    projectId: string,
    publicId: string,
    sessionId: string | null,
  ): Promise<Requirement> => {
    const params = sessionId ? { session_id: sessionId } : undefined;
    const { data } = await apiClient.post<Requirement>(
      `${base(projectId)}/${encodeURIComponent(publicId)}/accept`,
      null,
      { params },
    );
    return data;
  },

  /** POST — reject a suggested requirement (soft-delete + 'reject'
   *  audit kind). Same `sessionId` pass-through as accept. */
  rejectSuggestion: async (
    projectId: string,
    publicId: string,
    sessionId: string | null,
  ): Promise<void> => {
    const params = sessionId ? { session_id: sessionId } : undefined;
    await apiClient.post(
      `${base(projectId)}/${encodeURIComponent(publicId)}/reject`,
      null,
      { params },
    );
  },

  /** POST — dry-run the validator against `target`. No state change. */
  previewTierUpgrade: async (
    projectId: string,
    target: UpdateSpecBody,
  ): Promise<TierUpgradeReport> => {
    const { data } = await apiClient.post<TierUpgradeReport>(
      `${base(projectId)}/spec/preview-upgrade`,
      target,
    );
    return data;
  },

  /** PUT — commit the tier change. At Concept→Product the upgrade is
   *  non-blocking and the report comes back with `blocking=false`. At
   *  Regulated, blocking gaps reject with 409 — this method translates
   *  that into a typed `TierUpgradeBlockedError` so callers don't have
   *  to inspect axios internals. */
  updateSpec: async (
    projectId: string,
    body: UpdateSpecBody,
  ): Promise<TierUpgradeReport> => {
    try {
      const { data } = await apiClient.put<TierUpgradeReport>(
        `${base(projectId)}/spec`,
        body,
      );
      return data;
    } catch (err) {
      // axios isAxiosError is fine but we don't import the type to keep
      // the dependency surface small — duck-type instead.
      const e = err as {
        response?: { status?: number; data?: { detail?: TierUpgradeReport } };
      };
      if (e?.response?.status === 409 && e.response.data?.detail) {
        throw new TierUpgradeBlockedError(e.response.data.detail);
      }
      throw err;
    }
  },
};
